/**
 * useLookupLabelSelfHeal — miss-driven refresh of a lookup enum's cached
 * key→label pairs.
 *
 * A grid cell whose key is **absent** from the column's cached pair map is
 * proof that the cache is stale: the target table has gained a row since the
 * pairs were sampled. That is the only staleness signal the user can ever
 * produce for free, so it drives the refresh — one throttled background
 * `fetchEnumValues(..., 'lookup')` per column, after which the labels appear
 * in place. No button, no spinner, no state the user has to learn.
 *
 * What is NOT a miss (see `buildLookupLabelMap`'s absent-vs-null contract):
 *  - a key present with a `null` label — the target row exists and its label
 *    column is genuinely NULL;
 *  - a NULL/undefined cell — there is no key to look up;
 *  - ANY key, once the column's cache is `truncated` — the target outgrew the
 *    200-key cap, so a refetch is guaranteed to come back with the same 200
 *    and heal nothing. Without this gate a reference table that grows past
 *    the cap costs one query per column per throttle window forever.
 *
 * Cost when nothing is stale: one `Map.has` per lookup-column cell of the
 * VISIBLE window, memoized on the rows + label maps, bailing out of a column
 * on its first miss. No query of any kind fires until a miss exists — the
 * target table's column list (needed to resolve the label column) is fetched
 * lazily inside the refresh, not by an always-armed hook.
 *
 * Mounted by BOTH grids (CLAUDE.md § "Two Results Grids"): `ResultsGrid.tsx`
 * and `TableExplorerGrid.tsx`.
 */

import { useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { DatabaseMetaInfo, EnumLookupTarget } from '@/types/electron';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import { refreshLookupCacheForColumn } from '../utils/lookupCacheRefresh';
import type { LookupLabelInfo, LookupLabelMapColumn } from './useLookupLabelMaps';
import { useInvalidateMetaInfo } from './useMetaInfo';
import type { TableExplorerMetadataEditorProviders } from '../../table-explorer/components/tableExplorerHostPorts';

/**
 * The fetch, the throttle window and the reset helper live in
 * `utils/lookupCacheRefresh.ts` — shared with the SWR path (`useEnumValues`)
 * and the post-edit write-through (`useLookupCacheWriteThrough`) so all three
 * refresh reasons share ONE per-column throttle. Re-exported under the names
 * this hook introduced them with.
 */
export {
  LOOKUP_REFRESH_THROTTLE_MS as LOOKUP_SELF_HEAL_THROTTLE_MS,
  __resetLookupRefreshThrottleForTests as __resetLookupSelfHealThrottleForTests,
} from '../utils/lookupCacheRefresh';

/** A grid column plus the row-object key its cell values are read with. */
export type LookupSelfHealColumn = LookupLabelMapColumn & {
  /** Row-object property the grid reads this column's value from. */
  key: string;
};

/** One column that needs healing: its declaration identity + FK target. */
type LookupSelfHealMiss = {
  schema: string;
  table: string;
  column: string;
  target: EnumLookupTarget;
};

const EMPTY_MISSES: LookupSelfHealMiss[] = [];

export function useLookupLabelSelfHeal(args: {
  connectionId: string | null | undefined;
  database: string | null | undefined;
  engine: string | undefined;
  metaInfo: DatabaseMetaInfo | null | undefined;
  /** Per-column cached pairs, straight from `useLookupLabelMaps`. */
  lookupLabelInfos: Map<string, LookupLabelInfo>;
  columns: readonly LookupSelfHealColumn[];
  /** The rows the grid is actually rendering (its virtualized window). */
  rows: readonly Record<string, unknown>[];
  /** Host metadata provider (Azure Table): the refresh never reaches SQL metadata (S4 review C, M7). */
  metadataEditors?: TableExplorerMetadataEditorProviders;
}): void {
  const { connectionId, database, engine, metaInfo, lookupLabelInfos, columns, rows, metadataEditors } = args;
  const qc = useQueryClient();
  const { track } = useAnalytics();
  const invalidate = useInvalidateMetaInfo();

  // Read in the async refresh, never in the effect's dependency list: both
  // identities change on every render, and a fresh MetaInfo snapshot at fetch
  // time is exactly what the label-column resolution wants.
  const latest = useRef({ metaInfo, invalidate });
  latest.current = { metaInfo, invalidate };

  const misses = useMemo(() => {
    if (lookupLabelInfos.size === 0 || rows.length === 0) return EMPTY_MISSES;
    const found: LookupSelfHealMiss[] = [];
    for (const column of columns) {
      const info = lookupLabelInfos.get(column.id);
      if (!info?.target) continue;
      // A TRUNCATED cache already holds every key a lookup cache can hold, so
      // a missing key is not evidence the cache is stale — it is evidence the
      // target outgrew the 200-key cap. Refetching would return the same 200,
      // leave the same keys missing, and fire again on the next mount: one
      // query per column per throttle window, forever, healing nothing.
      // (SWR and the manual Refresh still run — only the miss trigger stops.)
      if (info.truncated) continue;
      if (!column.sourceTable || !column.sourceColumn) continue;
      for (const row of rows) {
        const value = row?.[column.key];
        // A NULL cell has no key to resolve; a key that IS in the map is a
        // hit even when its label is `null` (a known-NULL label).
        if (value == null) continue;
        if (info.labels.has(String(value))) continue;
        found.push({
          schema: column.sourceSchema ?? '',
          table: column.sourceTable,
          column: column.sourceColumn,
          target: info.target,
        });
        break; // one miss is enough — the whole column gets refreshed
      }
    }
    return found.length === 0 ? EMPTY_MISSES : found;
  }, [columns, rows, lookupLabelInfos]);

  useEffect(() => {
    if (misses.length === 0) return;
    if (!connectionId || !database || !engine) return;

    for (const miss of misses) {
      // Throttled + swallowed inside; a miss is only ever *evidence* of
      // staleness, so it never forces past a recent attempt.
      void refreshLookupCacheForColumn({
        queryClient: qc,
        connectionId,
        database,
        engine,
        schema: miss.schema,
        table: miss.table,
        column: miss.column,
        target: miss.target,
        getMetaInfo: () => latest.current.metaInfo,
        reason: 'miss',
        track,
        invalidate: (c, d) => latest.current.invalidate(c, d),
        metadataEditors,
      });
    }
  }, [misses, connectionId, database, engine, qc, track, metadataEditors]);
}
